import { useState, useMemo, useEffect } from 'react';
import { Search, ChevronUp, ChevronDown, Filter, Layers, List, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { getPositions, fetchApi } from '../api/client';
import Sparkline from './Sparkline';

function formatCurrency(value) {
  if (value === null || value === undefined) return 'N/A';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function formatPercent(value) {
  if (value === null || value === undefined) return 'N/A';
  return new Intl.NumberFormat('en-US', {
    style: 'percent',
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(value);
}

export default function PositionsTable({ positions: initialPositions }) {
  const [positions, setPositions] = useState(initialPositions || []);
  const [consolidated, setConsolidated] = useState(true); // Default to consolidated
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState('market_value');
  const [sortDirection, setSortDirection] = useState('desc');
  const [filterAccount, setFilterAccount] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [velocityData, setVelocityData] = useState({}); // ticker -> velocity info

  // Fetch positions when consolidated mode changes
  useEffect(() => {
    async function fetchPositions() {
      setLoading(true);
      try {
        const data = await getPositions(null, consolidated);
        setPositions(data.positions || []);
      } catch (err) {
        console.error('Error fetching positions:', err);
      } finally {
        setLoading(false);
      }
    }
    fetchPositions();
  }, [consolidated]);

  // Fetch velocity data for sparklines
  useEffect(() => {
    async function fetchVelocity() {
      try {
        const data = await fetchApi('/velocity');
        // Convert array to map by ticker
        const velocityMap = {};
        data.forEach(v => {
          velocityMap[v.ticker] = v;
        });
        setVelocityData(velocityMap);
      } catch (err) {
        console.error('Error fetching velocity data:', err);
      }
    }
    fetchVelocity();
  }, []);

  // Update positions when initialPositions changes (but only if not consolidated)
  useEffect(() => {
    if (!consolidated && initialPositions) {
      setPositions(initialPositions);
    }
  }, [initialPositions, consolidated]);

  // Get unique accounts and categories
  const accounts = useMemo(() => {
    return [...new Set(positions.map(p => p.account_id))].sort();
  }, [positions]);

  const categories = useMemo(() => {
    return [...new Set(positions.map(p => p.category).filter(Boolean))].sort();
  }, [positions]);

  // Filter and sort positions
  const filteredPositions = useMemo(() => {
    let result = [...positions];

    // Search filter
    if (search) {
      const searchLower = search.toLowerCase();
      result = result.filter(p => 
        p.ticker.toLowerCase().includes(searchLower) ||
        (p.category && p.category.toLowerCase().includes(searchLower)) ||
        p.account_id.toLowerCase().includes(searchLower)
      );
    }

    // Account filter (only when not consolidated)
    if (filterAccount && !consolidated) {
      result = result.filter(p => p.account_id === filterAccount);
    }

    // Category filter
    if (filterCategory) {
      result = result.filter(p => p.category === filterCategory);
    }

    // Sort
    result.sort((a, b) => {
      let aVal, bVal;
      
      // Handle velocity_7d specially - look up from velocityData
      if (sortField === 'velocity_7d') {
        aVal = velocityData[a.ticker]?.returns?.['7d'];
        bVal = velocityData[b.ticker]?.returns?.['7d'];
      } else {
        aVal = a[sortField];
        bVal = b[sortField];
      }

      // Handle null values
      if (aVal === null || aVal === undefined) aVal = sortDirection === 'asc' ? Infinity : -Infinity;
      if (bVal === null || bVal === undefined) bVal = sortDirection === 'asc' ? Infinity : -Infinity;

      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });

    return result;
  }, [positions, search, filterAccount, filterCategory, sortField, sortDirection, consolidated, velocityData]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? 
      <ChevronUp className="w-4 h-4" /> : 
      <ChevronDown className="w-4 h-4" />;
  };

  // Calculate totals
  const totals = useMemo(() => {
    return {
      marketValue: filteredPositions.reduce((sum, p) => sum + (p.market_value || 0), 0),
      costBasis: filteredPositions.reduce((sum, p) => sum + (p.shares * (p.cost_basis_per_share || 0)), 0),
      gainLoss: filteredPositions.reduce((sum, p) => sum + (p.gain_loss_dollars || 0), 0),
    };
  }, [filteredPositions]);

  return (
    <div className="space-y-4 animate-fade-in">
      {/* View Toggle & Filters */}
      <div className="flex flex-wrap gap-4 items-center">
        {/* Consolidated Toggle */}
        <div className="flex items-center gap-2 bg-slate-800 rounded-lg p-1">
          <button
            onClick={() => setConsolidated(true)}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              consolidated 
                ? 'bg-sky-500 text-white' 
                : 'text-slate-400 hover:text-slate-200'
            }`}
          >
            <Layers className="w-4 h-4" />
            Consolidated
          </button>
          <button
            onClick={() => setConsolidated(false)}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              !consolidated 
                ? 'bg-sky-500 text-white' 
                : 'text-slate-400 hover:text-slate-200'
            }`}
          >
            <List className="w-4 h-4" />
            By Account
          </button>
        </div>

        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search positions..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-slate-400" />
          {!consolidated && (
            <select
              value={filterAccount}
              onChange={(e) => setFilterAccount(e.target.value)}
              className="min-w-[150px]"
            >
              <option value="">All Accounts</option>
              {accounts.map(acc => (
                <option key={acc} value={acc}>{acc}</option>
              ))}
            </select>
          )}
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="min-w-[150px]"
          >
            <option value="">All Categories</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Info Banner for Consolidated View */}
      {consolidated && (
        <div className="p-3 bg-sky-500/10 border border-sky-500/30 rounded-lg text-sm text-sky-300">
          <strong>Consolidated View:</strong> Positions are aggregated by ticker across all accounts. 
          Target weights and rebalancing suggestions apply to your total holdings.
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center py-8">
          <div className="spinner w-6 h-6"></div>
        </div>
      )}

      {/* Table */}
      {!loading && (
        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl shadow-xl overflow-x-auto">
          <table className="w-full">
              <thead>
                <tr>
                  <th 
                    className="cursor-pointer hover:text-slate-200 w-16"
                    onClick={() => handleSort('ticker')}
                  >
                    <div className="flex items-center gap-1">
                      Ticker <SortIcon field="ticker" />
                    </div>
                  </th>
                  <th 
                    className="cursor-pointer hover:text-slate-200 w-32"
                    onClick={() => handleSort('account_id')}
                  >
                    <div className="flex items-center gap-1">
                      {consolidated ? 'Accts' : 'Account'} <SortIcon field="account_id" />
                    </div>
                  </th>
                  <th 
                    className="cursor-pointer hover:text-slate-200 w-24"
                    onClick={() => handleSort('category')}
                  >
                    <div className="flex items-center gap-1">
                      Category <SortIcon field="category" />
                    </div>
                  </th>
                  <th 
                    className="cursor-pointer hover:text-slate-200 text-right w-20"
                    onClick={() => handleSort('shares')}
                  >
                    <div className="flex items-center justify-end gap-1">
                      Shares <SortIcon field="shares" />
                    </div>
                  </th>
                  <th 
                    className="cursor-pointer hover:text-slate-200 text-right w-24"
                    onClick={() => handleSort('current_price')}
                  >
                    <div className="flex items-center justify-end gap-1">
                      Price <SortIcon field="current_price" />
                    </div>
                  </th>
                  <th className="text-center w-20">
                    <div className="flex items-center justify-center gap-1">
                      30d
                    </div>
                  </th>
                  <th 
                    className="cursor-pointer hover:text-slate-200 text-right w-16"
                    onClick={() => handleSort('velocity_7d')}
                  >
                    <div className="flex items-center justify-end gap-1">
                      7d <SortIcon field="velocity_7d" />
                    </div>
                  </th>
                  <th 
                    className="cursor-pointer hover:text-slate-200 text-right w-28"
                    onClick={() => handleSort('market_value')}
                  >
                    <div className="flex items-center justify-end gap-1">
                      Value <SortIcon field="market_value" />
                    </div>
                  </th>
                  <th 
                    className="cursor-pointer hover:text-slate-200 text-right w-24"
                    onClick={() => handleSort('gain_loss_dollars')}
                  >
                    <div className="flex items-center justify-end gap-1">
                      G/L $ <SortIcon field="gain_loss_dollars" />
                    </div>
                  </th>
                  <th 
                    className="cursor-pointer hover:text-slate-200 text-right w-16"
                    onClick={() => handleSort('gain_loss_percent')}
                  >
                    <div className="flex items-center justify-end gap-1">
                      G/L % <SortIcon field="gain_loss_percent" />
                    </div>
                  </th>
                  <th 
                    className="cursor-pointer hover:text-slate-200 text-right w-16"
                    onClick={() => handleSort('portfolio_weight')}
                  >
                    <div className="flex items-center justify-end gap-1">
                      Wt % <SortIcon field="portfolio_weight" />
                    </div>
                  </th>
                  <th 
                    className="cursor-pointer hover:text-slate-200 text-right w-16"
                    onClick={() => handleSort('portfolio_target_weight')}
                  >
                    <div className="flex items-center justify-end gap-1">
                      Tgt % <SortIcon field="portfolio_target_weight" />
                    </div>
                  </th>
                  <th 
                    className="cursor-pointer hover:text-slate-200 text-right w-24"
                    onClick={() => handleSort('variance_dollars')}
                  >
                    <div className="flex items-center justify-end gap-1">
                      Var $ <SortIcon field="variance_dollars" />
                    </div>
                  </th>
                  <th className="text-center w-16">Action</th>
                  <th className="text-right w-28">Rebalance</th>
                </tr>
              </thead>
              <tbody>
                {filteredPositions.map((pos, idx) => (
                  <tr key={consolidated ? pos.ticker : `${pos.account_id}-${pos.ticker}`}>
                    <td className="font-medium text-slate-200">{pos.ticker}</td>
                    <td className="text-slate-400 text-xs max-w-[120px] truncate" title={pos.account_id}>
                      {pos.account_id}
                    </td>
                    <td>
                      {pos.category && (
                        <span className="badge badge-info">{pos.category}</span>
                      )}
                    </td>
                    <td className="text-right font-mono">{pos.shares.toFixed(2)}</td>
                    <td className="text-right font-mono">{formatCurrency(pos.current_price)}</td>
                    <td className="text-center">
                      {velocityData[pos.ticker]?.sparkline ? (
                        <Sparkline 
                          data={velocityData[pos.ticker].sparkline} 
                          width={80} 
                          height={24}
                          strokeWidth={1.5}
                        />
                      ) : (
                        <span className="text-slate-600">—</span>
                      )}
                    </td>
                    <td className="text-right">
                      {velocityData[pos.ticker]?.returns?.['7d'] != null ? (
                        <div className="flex items-center justify-end gap-1">
                          {velocityData[pos.ticker].returns['7d'] > 2 ? (
                            <TrendingUp className="w-3 h-3 text-emerald-400" />
                          ) : velocityData[pos.ticker].returns['7d'] < -2 ? (
                            <TrendingDown className="w-3 h-3 text-red-400" />
                          ) : (
                            <Minus className="w-3 h-3 text-slate-500" />
                          )}
                          <span className={`font-mono text-sm ${
                            velocityData[pos.ticker].returns['7d'] > 0 
                              ? 'text-emerald-400' 
                              : velocityData[pos.ticker].returns['7d'] < 0 
                                ? 'text-red-400' 
                                : 'text-slate-500'
                          }`}>
                            {velocityData[pos.ticker].returns['7d'] > 0 ? '+' : ''}
                            {velocityData[pos.ticker].returns['7d'].toFixed(1)}%
                          </span>
                        </div>
                      ) : (
                        <span className="text-slate-600">—</span>
                      )}
                    </td>
                    <td className="text-right font-mono font-medium">{formatCurrency(pos.market_value)}</td>
                    <td className={`text-right font-mono ${(pos.gain_loss_dollars || 0) >= 0 ? 'value-positive' : 'value-negative'}`}>
                      {pos.gain_loss_dollars != null ? formatCurrency(pos.gain_loss_dollars) : '—'}
                    </td>
                    <td className={`text-right font-mono ${(pos.gain_loss_percent || 0) >= 0 ? 'value-positive' : 'value-negative'}`}>
                      {pos.gain_loss_percent != null ? formatPercent(pos.gain_loss_percent) : '—'}
                    </td>
                    <td className="text-right font-mono text-slate-400">{formatPercent(pos.portfolio_weight)}</td>
                    <td className="text-right font-mono text-slate-400">{formatPercent(pos.portfolio_target_weight)}</td>
                    <td className={`text-right font-mono text-sm ${
                      pos.variance_dollars > 0 ? 'text-amber-400' : 
                      pos.variance_dollars < 0 ? 'text-sky-400' : 
                      'text-slate-500'
                    }`}>
                      {pos.variance_dollars != null ? (
                        <span>{pos.variance_dollars > 0 ? '+' : ''}{formatCurrency(pos.variance_dollars)}</span>
                      ) : '—'}
                    </td>
                    <td className="text-center">
                      {pos.rebalance_action && (
                        <span className={`badge ${
                          pos.rebalance_action === 'BUY' ? 'badge-success' :
                          pos.rebalance_action === 'TRIM' ? 'badge-danger' :
                          'badge-info'
                        }`}>
                          {pos.rebalance_action}
                        </span>
                      )}
                    </td>
                    <td className="text-right">
                      {pos.rebalance_action && pos.rebalance_action !== 'HOLD' && pos.rebalance_dollars ? (
                        <div className="text-sm">
                          <span className={`font-mono ${pos.rebalance_dollars > 0 ? 'text-emerald-400' : 'text-amber-400'}`}>
                            {pos.rebalance_dollars > 0 ? '+' : ''}{formatCurrency(pos.rebalance_dollars)}
                          </span>
                          {pos.rebalance_shares && pos.current_price && (
                            <span className="text-slate-500 text-xs block">
                              ~{Math.abs(pos.rebalance_shares).toFixed(1)} shares
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-slate-600">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-slate-800/80 font-medium">
                <tr>
                  <td colSpan="5" className="text-slate-300">
                    Total ({filteredPositions.length} {consolidated ? 'tickers' : 'positions'})
                  </td>
                  <td></td>
                  <td></td>
                  <td className="text-right font-mono text-slate-200">{formatCurrency(totals.marketValue)}</td>
                  <td className={`text-right font-mono ${totals.gainLoss >= 0 ? 'value-positive' : 'value-negative'}`}>
                    {formatCurrency(totals.gainLoss)}
                  </td>
                  <td colSpan="6"></td>
                </tr>
              </tfoot>
            </table>
        </div>
      )}
    </div>
  );
}
